import type { TranslationTree } from '../../types';

export const analytics: TranslationTree = {
  analytics: {
    dashboard: {
      title: 'Analytics',
      description:
        'Compare your fleet references, measure their production weight, and pinpoint the contexts where they perform best.',
      breadcrumb: {
        analytics: 'Advanced Analytics',
      },
      sections: {
        performanceSummary: 'Performance Overview',
        performanceSummaryDescription:
          'Global score and key performance indicators for the reference.',
        keyMetrics: 'Key Metrics',
        keyMetricsDescription: 'Overview of the selected reference performance.',
        performanceAnalysis: 'Performance Analysis',
        performanceAnalysisDescription:
          'Comparison with other references and process configuration.',
        environmentalImpact: 'Environmental Impact',
        environmentalImpactDescription:
          'Comparative performance analysis by machine, material, and operator.',
        detailedBreakdown: 'Detailed Breakdown',
        detailedBreakdownDescription: 'Cycle distribution, machine usage, and materials.',
        portfolioTrends: 'Portfolio & Trends',
        portfolioTrendsDescription: 'Reference portfolio overview and evolution over time.',
      },
      controls: {
        referenceLabel: 'Tool reference',
        lastUpdated: 'Last updated: {{date}}',
        pieceCount: '{{count}} piece(s) produced',
      },
      summary: {
        uniqueTools: 'Unique tools',
        totalTraces: 'Total traces',
        ranking: 'Ranking',
        rankValue: '{{rank}} of {{total}}',
        noRanking: 'Not ranked',
        avgTracesPerTool: 'Avg. traces / tool',
      },
      keyIndicators: {
        title: 'Key Indicators',
        description: 'Quick overview of aggregated metrics for all traces of this reference.',
        noData: 'No data available for this reference.',
        noDataDescription:
          'No traces found for this reference. Select another reference or refresh the data.',
        totalDuration: 'Total Duration',
        totalDurationDesc: 'Across {{count}} traces',
        durationByPiece: 'Duration per Piece',
        durationByPieceDesc: 'Average',
        numberOfPieces: 'Number of Pieces',
        numberOfPiecesDesc: 'Produced',
        volumeRemoved: 'Volume Removed',
        volumeRemovedDesc: 'Material machined',
        distanceTraveled: 'Distance Traveled',
        distanceTraveledDesc: 'Total machining',
        malround: 'Malround',
        malroundDesc: 'Average',
      },
      globalPerformance: {
        title: 'Global Performance',
        description:
          'Usage distribution and performance comparison across all tools of this reference.',
        usageDistribution: 'Usage Distribution',
        performanceComparison: 'Performance Comparison',
        toolComparison: '{{count}} tool(s) compared',
        dimensions: {
          material: 'Material',
          machine: 'Machine',
          lubrication: 'Lubrication',
          toolHolder: 'Tool Holder',
        },
        metrics: {
          duration: 'Total Duration',
          pieces: 'Number of Pieces',
          volume: 'Volume Removed',
          distance: 'Distance Traveled',
          malround: 'Malround',
        },
      },
      complexCharts: {
        legend: {
          more: 'etc…',
        },
        title: 'Advanced Charts',
        description: 'Create custom charts with multi-dimensional filters to analyze data.',
        addChart: 'Add Chart',
        exportConfig: 'Export Configuration',
        importConfig: 'Import Configuration',
        saveAsDefault: 'Save as Default',
        clearDefault: 'Clear Default',
        exportSuccess: 'Configuration exported successfully',
        importSuccess: 'Configuration imported successfully',
        saveSuccess: 'Configuration saved as default',
        clearSuccess: 'Default configuration cleared',
        importError: 'Failed to import configuration. Please check the file format.',
        exportError: 'Failed to export configuration.',
        saveError: 'Failed to save configuration as default.',
        clearError: 'Failed to clear default configuration.',
        example: 'Example',
        removeChart: 'Remove Chart',
        editTitle: 'Edit Title',
        showingTraces: 'Showing {{filtered}} of {{total}} traces',
        noData: 'No data available for the selected configuration.',
        noDataForConfig: 'No data available for the selected configuration.',
        chartError: 'Chart rendering error',
        chartErrorDescription: 'An unexpected error occurred while rendering this chart.',
        chartErrorTryAgain: 'Try again',
        filters: {
          title: 'Filters',
          mode: 'Mode',
          filterMode: 'Filter Mode:',
          modeLabel: 'Filter mode',
          allTraces: 'ALL TRACES',
          anyTrace: 'ANY TRACE',
          clearAll: 'Clear All',
          machines: 'Machines',
          materials: 'Materials',
          toolHolders: 'Tool Holders',
          lubrications: 'Lubrications',
          selectMachines: 'Select machines',
          selectMaterials: 'Select materials',
          selectToolHolders: 'Select tool holders',
          selectLubrications: 'Select lubrications',
          activeFilters: 'Active Filters',
          noFilters: 'No filters applied',
          noMachines: 'No machines',
          noMaterials: 'No materials',
          noToolHolders: 'No tool holders',
          noLubrications: 'No lubrications',
          machineLabel: 'Machine:',
          materialLabel: 'Material:',
          holderLabel: 'Holder:',
          lubricationLabel: 'Lubrication:',
          addFilter: 'Add filter',
          allData: 'All data is shown — add a rule to narrow it',
          minMaxError: 'min > max',
          filterCount: '{{count}} active',
          sectionCategorical: 'Categorical',
          sectionNumeric: 'Numeric range',
          sectionDate: 'Date range',
          removeRule: 'Remove rule',
          negateRule: 'Negate rule',
          negateTitle: 'Toggle negation (≠)',
          valuePlaceholder: 'Value…',
          kindBadge: {
            categorical: 'CAT',
            numeric: 'NUM',
            date: 'DATE',
          },
          operators: {
            in: 'is',
            not_in: 'is not',
            range: 'between',
            gte: '≥',
            lte: '≤',
            date_range: 'between',
            after: 'after',
            before: 'before',
          },
          dimensions: {
            machine: 'Machine',
            material: 'Material',
            lubrication: 'Lubrication',
            toolHolder: 'Tool Holder',
            totalMachiningDuration: 'Total duration',
            pieceMachiningDuration: 'Duration per piece',
            machinedVolume: 'Volume removed',
            machinedDistance: 'Distance traveled',
            machinedPieces: 'Pieces machined',
            malround: 'Runout',
            toolState: 'Tool State',
            startedAt: 'Started at',
            endedAt: 'Ended at',
            vc: 'Cutting Speed (vc)',
            fz: 'Feed per Tooth (fz)',
            ap: 'Axial Depth (ap)',
            ae: 'Radial Depth (ae)',
            operation: 'Operation',
            clampingType: 'Clamping Type',
            reference: 'Reference',
          },
        },
        tooltip: {
          tool: 'Tool',
          trace: 'Trace',
          traceIndex: 'Trace Index',
          category: 'Category',
          value: 'Value',
          count: 'Count',
          frequency: 'Frequency',
          range: 'Range',
          aggregatedFrom: 'Aggregated from {{count}} traces',
          aggregatedFromTools: 'Aggregated from {{count}} tools',
          ofTotal: '{{percent}}% of total',
          ofData: '{{percent}}% of data',
          min: 'Min',
          max: 'Max',
          median: 'Median',
          mean: 'Mean',
          q1: 'Q1',
          q3: 'Q3',
          outliers: 'Outliers',
          samples: 'Samples',
          reference: 'Reference',
          toolCount: '{{count}} tool(s)',
          traceCount: '{{count}} trace(s)',
          vc: 'Cutting Speed (vc)',
          fz: 'Feed per Tooth (fz)',
          ap: 'Axial Depth (ap)',
          ae: 'Radial Depth (ae)',
          operation: 'Operation',
          clampingType: 'Clamping Type',
        },
        configuration: {
          title: 'Configuration',
          chartType: 'Chart Type',
          xAxis: 'X Axis',
          yAxis: 'Y Axis',
          groupBy: 'Group By',
          aggregation: 'Aggregation',
          aggregationLevel: 'Aggregation Level',
          byTool: 'By Tool',
          byTrace: 'By Trace',
          byReference: 'By Reference',
          advancedOptions: 'Advanced Options',
          binCount: 'Bin Count',
          bins: 'bins',
          bins5: '5 bins',
          bins10: '10 bins',
          bins20: '20 bins',
          bins50: '50 bins',
          showGridLines: 'Show Grid Lines',
          showDataLabels: 'Show Data Labels',
          showTrendLine: 'Show Trend Line',
          colorBy: 'Color By',
          sortOrder: 'Sort Order',
          ascending: 'Ascending',
          descending: 'Descending',
          alphabetical: 'Alphabetical',
          radarMode: 'Radar Mode',
          radarMaxItems: 'Max Items',
          radarModes: {
            categories: 'Compare Categories',
            metrics: 'Compare Metrics',
          },
          showOutliers: 'Show Outliers',
          sortAscending: 'Ascending',
          sortDescending: 'Descending',
          sortAlphabetical: 'Alphabetical',
          types: {
            scatter: 'Scatter Plot',
            bar: 'Bar Chart',
            histogram: 'Histogram',
            pie: 'Pie Chart',
            radar: 'Radar Chart',
            boxplot: 'Box Plot',
          },
          metrics: {
            totalMachiningDuration: 'Total Duration',
            pieceMachiningDuration: 'Duration per Piece',
            machinedPieces: 'Number of Pieces',
            machinedVolume: 'Volume Removed',
            machinedDistance: 'Distance Traveled',
            durationByPiece: 'Duration per Piece',
            numberOfPieces: 'Number of Pieces',
            volumeRemoved: 'Volume Removed',
            distanceTraveled: 'Distance Traveled',
            malround: 'Malround',
            machine: 'Machine',
            material: 'Material',
            toolHolder: 'Tool Holder',
            lubrication: 'Lubrication',
            operator: 'Operator',
            toolId: 'Tool ID',
            traceId: 'Trace ID',
            reference: 'Reference',
            toolState: 'Tool State',
            traceCount: 'Trace Count',
            vc: 'Cutting speed (Vc)',
            fz: 'Feed per tooth (fz)',
            ap: 'Axial depth of cut (ap)',
            ae: 'Radial depth of cut (ae)',
            operation: 'Operation',
            clampingType: 'Clamping type',
          },
          noData: 'No data available for the selected configuration.',
          aggregations: {
            sum: 'Sum',
            mean: 'Mean',
            avg: 'Average',
            median: 'Median',
            min: 'Minimum',
            max: 'Maximum',
            count: 'Count',
            p90: '90th Percentile',
            p95: '95th Percentile',
          },
        },
        defaults: {
          xAxisLabel: 'X Axis',
          yAxisLabel: 'Y Axis',
          categoryLabel: 'Category',
          valueLabel: 'Value',
          traceIndexLabel: 'Trace Index',
          frequency: 'Frequency',
          frequencyLabel: 'Frequency (Count)',
          unknown: 'Unknown',
        },
        axis: {
          traceIndex: 'Trace Index',
          traceNumber: 'Trace {{number}}',
        },
        widget: {
          viewOnly: 'View only',
        },
        export: {
          button: 'Export as PNG',
          success: 'Chart exported successfully',
          error: 'Error during export',
        },
        stale: {
          code: 'WARN_STALE_METRIC',
          title: 'Chart needs reconfiguration',
          editConfig: 'Edit configuration',
          dismiss: 'Dismiss',
        },
        emptyState: {
          filterCode: 'ERR_FILTER_NO_MATCH',
          filterTitle: 'No traces matched',
          filterBody: 'Active filters returned 0 results. Try loosening a constraint.',
          noDataCode: 'AWAIT_DATA',
          noDataTitle: 'No data for this period',
          noDataBody: 'No traces available for the selected reference and date range.',
          resetFilters: 'Reset filters',
          editConfig: 'Edit configuration',
          configure: 'Configure chart',
        },
        removeDialog: {
          title: 'Remove this chart?',
          subtitle: 'Destructive · Undo available (5s)',
          body: '{{title}} will be removed from this dashboard.',
          confirm: 'Remove chart',
          cancel: 'Cancel',
        },
        clearDefaultDialog: {
          title: 'Clear saved default?',
          subtitle: 'Destructive · Cannot be undone',
          body: 'Your saved dashboard layout will be deleted.',
          confirm: 'Clear default',
          cancel: 'Cancel',
        },
        undoRemove: {
          removed: '{{title}} removed',
          undo: 'Undo',
          dismiss: 'Dismiss',
        },
        dragHandle: 'Drag to reorder',
        newChart: 'New chart',
        chartNumber: 'Chart {{number}}',
        layoutLabel: 'Chart size',
        layout: {
          sm: 'Small',
          md: 'Medium',
          lg: 'Large',
          full: 'Full width',
        },
      },
      shortcuts: {
        refresh: 'Press R to refresh',
      },
      actions: {
        refresh: 'Refresh data',
        refreshing: 'Refreshing…',
        compareReferences: 'Compare References',
      },
      filters: {
        title: 'Filters',
        clearAll: 'Clear filters',
        machine: {
          label: 'Machine',
          placeholder: 'Select a machine',
          all: 'All machines',
        },
        lubrication: {
          label: 'Lubrication',
          placeholder: 'Select a lubrication',
          all: 'All lubrications',
        },
        material: {
          label: 'Material',
          placeholder: 'Select a material',
          all: 'All materials',
        },
      },
      common: {
        minutes: '{{value}} min',
        noData: 'No data available for the selected period.',
        notSpecified: 'Not specified',
      },
      samples: {
        fallback: 'No sample available',
        traces: '{{count}} analyzed traces',
        tracesFiltered: '{{filtered}} analyzed traces ({{total}} total)',
        occurrences: '{{count}} occurrences',
        cycles: '{{count}} cycles',
        trend: '{{count}} periods',
        references: '{{count}} compared references',
      },
      error: {
        loadFailure: 'Unable to load analytics data. Showing simulated values.',
        title: 'Unable to retrieve analytics data',
        detailsTitle: 'Technical details',
        detailName: 'Type: {{value}}',
        detailCode: 'Code: {{value}}',
        detailMessage: 'Message: {{value}}',
        detailTimestamp: 'Timestamp: {{value}}',
        detailsFallback: 'No additional details were provided.',
        retry: 'Retry',
        noRef: 'No reference available. Please check that tools have been recorded.',
        unknown: 'An unknown error occurred.',
      },
      stats: {
        totalPieces: {
          title: 'Parts produced',
          helper: 'Across {{count}} traces.',
          tooltip:
            'Total number of parts machined with this tool reference according to recorded traces.',
        },
        cycleEfficiency: {
          title: 'Cycle efficiency',
          helper: 'Machined time vs total cycle duration',
          tooltip:
            'Ratio between actual part machining time and total cycle duration, expressed as a percentage. A high rate indicates better machine utilization.',
        },
        averageCycle: {
          title: 'Average cycle time',
          helper: 'Average machining duration per trace',
          tooltip:
            'Average time required to complete a machining cycle with this tool. Helps estimate production times.',
        },
        malround: {
          title: 'Average runout',
          helper: 'Surface metric (lower is better)',
          tooltip:
            'Measurement of the circularity of the machined part. The lower the value, the better the surface quality.',
        },
        downtime: {
          title: 'Downtime rate',
          helper: 'Incidents per ten recent traces',
          value: '{{value}} / 10 traces',
          tooltip:
            'Number of incidents or tool changes occurred per ten traces. A high rate may indicate maintenance needs or process issues.',
        },
        operatorCoverage: {
          title: 'Operator coverage',
          empty: '—',
          valueSingular: '{{count}} operator',
          valuePlural: '{{count}} operators',
          helperEmpty: 'No leading operator recorded.',
          helperWithShare: 'Primary operator: {{operator}} ({{share}}%)',
          helperWithoutShare: 'Primary operator: {{operator}}',
          tooltip:
            'Number of different operators who used this reference and identification of the primary operator. Helps track team versatility.',
        },
        totalVolume: {
          title: 'Total machined volume',
          helper: 'Cumulative volume across all traces',
          value: '{{value}} cm³',
          tooltip:
            'Total volume of material removed with this tool reference. Key indicator of tool wear and load.',
        },
        averageVolume: {
          title: 'Average volume per trace',
          helper: 'Average volume per machining cycle',
          value: '{{value}} cm³',
          tooltip: 'Average volume of material removed per trace. Helps estimate load per cycle.',
        },
        totalDistance: {
          title: 'Total distance traveled',
          helper: 'Cumulative distance across all traces',
          value: '{{value}} mm',
          tooltip:
            'Total distance traveled by the tool during machining. Important factor for anticipating tool wear.',
        },
        averageDistance: {
          title: 'Average distance per trace',
          helper: 'Average distance per machining cycle',
          value: '{{value}} mm',
          tooltip:
            'Average distance traveled by the tool per trace. Helps estimate stress per cycle.',
        },
      },
      comparison: {
        title: 'Reference comparison',
        description: 'See how this reference positions itself versus the rest of the portfolio.',
        currentBadge: 'Current',
        rank: 'Ranked #{{rank}} out of {{total}} tracked references.',
        rankUnavailable: 'Reference ranking unavailable.',
      },
      process: {
        title: 'Process configuration',
        description: 'Most frequent configuration elements observed recently.',
        lubrication: 'Lubrication',
        holder: 'Tool holder',
        clamping: 'Clamping',
        notProvided: 'Not provided',
        brandVariety: '{{count}} brand(s)',
        throughputShare: 'Production share',
        throughputDetail: '{{share}} of total parts',
      },
      cycles: {
        title: 'Cycle duration distribution',
        description: 'Understand cycle dispersion to detect anomalies and plan maintenance.',
        emptyLoading: 'Loading cycle data…',
        xLabel: 'Cycle duration [min]',
        yLabel: 'Number of machining cycles',
        emptyData: 'No cycle duration available for this reference.',
      },
      machines: {
        title: 'Machine distribution',
        description: 'Machines most associated with this reference.',
        noActivity: 'No machine activity recorded for this reference.',
        topInfo: '{{machine}}: {{share}}% of recent traces.',
        emptyDistribution: 'No machine distribution available.',
        empty: 'No machine data available for this reference.',
      },
      operators: {
        title: 'Operator distribution',
        description: 'Operators most frequently involved on this reference.',
        listEmpty: 'No operator data recorded for this reference.',
        empty: 'No operator data available for this reference.',
      },
      materials: {
        title: 'Material distribution',
        description: 'Most machined materials with the selected reference.',
        noInfo: 'No material information available for this reference.',
        infoSingular: '{{count}} material machined recently.',
        infoPlural: '{{count}} materials machined recently.',
        emptyDistribution: 'No material distribution available for this reference.',
        empty: 'No material data available for this reference.',
      },
      changeReasons: {
        title: 'Intervention reasons',
        description: 'Most frequent reasons logged during tool status changes.',
        empty: 'No reason recorded.',
        listEmpty: 'No change reason recorded yet.',
        downtimeRate: 'Downtime rate: {{rate}}',
      },
      portfolio: {
        title: 'Reference portfolio',
        description: 'Production share by reference across the organization.',
      },
      empty: {
        noComparison: 'No comparison data available for the selected references.',
        selectReference: 'Select a reference to display analytics.',
      },
      trend: {
        title: 'Production trend',
        description: 'Split between productive minutes and downtime recorded recently.',
        producedSeries: 'Productive time (min)',
        downtimeSeries: 'Non-productive time (min)',
        empty: 'Not enough data to display the trend.',
      },
      export: {
        downloadPNG: 'Download as PNG',
        downloadCSV: 'Export data (CSV)',
        share: 'Share',
        shareText: 'Check out this chart:',
      },
      insights: {
        title: 'Insights and Recommendations',
        description: 'Key points automatically identified from your data.',
        highEfficiency: 'Excellent cycle efficiency of {{efficiency}}% - Great performance!',
        lowEfficiency:
          'Cycle efficiency of {{efficiency}}% - Room for improvement in quality or configuration.',
        topPerformer: 'This reference is #1 in production volume - Top performer!',
        goodRank: 'This reference is in the top {{rank}} - Good relative performance.',
        highDowntime:
          '{{downtime}} incidents per 10 traces - High downtime rate, check recurring causes.',
        noDowntime: 'No incidents detected - Stable and reliable process.',
        improvingTrend: 'Production up {{change}}% recently - Positive trend!',
        decliningTrend: 'Production down {{change}}% recently - Trend to monitor.',
        highOperatorCoverage: '{{count}} different operators use this reference - Wide adoption.',
        singleOperator: 'Used only by {{operator}} - Consider training other operators.',
        singleMachine: 'Used only on {{machine}} - Limited to a single machine.',
        lowDataVolume:
          'Limited data available - Increase the number of traces for more reliable analyses.',
        priority: {
          critical: 'Critical',
          attention: 'Attention',
          optimization: 'Optimization',
        },
        category: {
          performance: 'Performance',
          trend: 'Trend',
          anomaly: 'Anomaly',
          tip: 'Tip',
        },
      },
      performanceSummary: {
        globalScore: 'Global Score',
        classification: {
          excellent: 'Excellent',
          good: 'Good',
          average: 'Average',
          'needs-improvement': 'Needs Improvement',
        },
        efficiency: 'Efficiency',
        quality: 'Quality',
        reliability: 'Reliability',
        aboveAverage: 'Above average',
        belowAverage: 'Below average',
        excellentQuality: 'Excellent quality',
        improvableQuality: 'Improvable quality',
        downtimePer10: 'incidents per 10 traces',
        ranking: 'Ranking',
        productionShare: 'Production share',
      },
      environmentalImpact: {
        machineAnalysis: 'Machine Analysis',
        materialAnalysis: 'Material Analysis',
        operatorAnalysis: 'Operator Analysis',
        malround: 'Malround',
        efficiency: 'Efficiency',
        incidents: 'Incidents',
        traces: 'traces',
        pieces: 'pieces',
        noData: 'No data available for this reference.',
        optimalConfig: 'Optimal Configuration Identified',
        optimalConfigDescription:
          'Machine-material combination delivering the best observed performance.',
        machine: 'Machine',
        material: 'Material',
      },
      subtitle: 'Performance analysis by tool reference',
      managepanels: 'Manage panels',
      classicView: 'Classic view',
      refresh: 'Refresh',
      loading: 'Loading tools…',
      ref: {
        label: 'Reference',
        avgPieces: 'avg pcs',
        tools: 'tools',
        states: {
          new: 'new',
          active: 'active',
          worn: 'worn',
          broken: 'broken',
          broken_early: 'broken early',
          unknown: 'unknown',
        },
      },
      panel: {
        noselection: {
          title: 'No panels selected',
          desc: 'Click Manage panels to add panels to your dashboard.',
        },
        lives: {
          name: 'Tool Lives',
          desc: 'Pieces produced per tool, sorted by performance.',
        },
        anomalies: {
          name: 'Anomalies',
          desc: 'Tools with significantly below-average piece count.',
        },
        machine: {
          name: 'By Machine',
          desc: 'Average pieces produced grouped by machine.',
        },
        operator: {
          name: 'By Operator',
          desc: 'Average pieces produced grouped by operator.',
        },
        material: {
          name: 'By Material',
          desc: 'Average pieces produced grouped by material.',
        },
        vc: {
          name: 'Cutting Speed',
          desc: 'Scatter of tool life vs cutting speed (Vc).',
        },
        fz: {
          name: 'Feed Rate',
          desc: 'Scatter of tool life vs feed per tooth (fz).',
        },
        ap: {
          name: 'Depth of Cut',
          desc: 'Scatter of tool life vs axial depth of cut (ap).',
        },
      },
      drawer: {
        title: 'Manage panels',
        group: {
          overview: 'Overview',
          context: 'Context breakdown',
          params: 'Parameter analysis',
        },
      },
      lives: {
        columns: {
          tool: 'Tool',
          pieces: 'Pieces produced',
          machine: 'Machine',
        },
        legend: {
          new: 'New',
          active: 'Active',
          worn: 'Worn',
          broken: 'Broken',
        },
        unit: {
          pieces: 'pcs',
        },
      },
      anomalies: {
        col: {
          tool: 'Tool',
          pieces: 'Pieces',
          vsAvg: 'vs avg',
          machine: 'Machine',
          operator: 'Operator',
          material: 'Material',
          vc: 'Vc',
          status: 'Status',
        },
        badge: {
          broken: 'Broken',
          investigate: 'Investigate',
        },
      },
      scatter: {
        nodata: 'No data available for the selected axis.',
        pieces: 'Pieces',
        axisLabels: {
          vc: 'Cutting speed (Vc)',
          fz: 'Feed per tooth (fz)',
          ap: 'Depth of cut (ap)',
        },
        note: {
          vc: 'Higher Vc (>200 m/min) may reduce tool life. Sweet spot zone highlighted.',
          fz: 'Optimal feed per tooth balances productivity and tool wear.',
          ap: 'Deeper cuts increase load — monitor tool life carefully.',
        },
      },
      grouped: {
        columns: {
          machine: 'Machine',
          operator: 'Operator',
          material: 'Material',
          avgPieces: 'Avg pieces / tool',
          tools: 'Tools',
        },
        note: {
          machine: '{{top}} yields {{ratio}}× more pieces than {{bottom}}.',
          operator: '{{top}} produces {{ratio}}× more pieces than {{bottom}}.',
          material: '{{top}} allows {{ratio}}× more pieces than {{bottom}}.',
        },
      },
      kpi: {
        avgLife: 'Avg tool life',
        avgLifeSub: 'best: {{best}} · worst: {{worst}}',
        totalPieces: 'Total pieces',
        totalPiecesSub: 'across {{n}} tools',
        totalTime: 'Total machining time',
        totalTimeSub: 'avg {{n}} min / tool',
        avgTimePerPiece: 'Avg time / piece',
        totalVolume: 'Volume removed',
        totalVolumeSub: 'avg {{n}} cm³ / tool',
        avgMalround: 'Avg malround',
        avgMalroundRange: 'range {{min}} – {{max}}',
        unit: {
          piecesPerTool: 'pieces / tool',
          pieces: 'pieces',
          minutes: 'minutes',
          secPerPiece: 's / piece',
          cm3: 'cm³ total',
          um: 'µm',
        },
      },
    },
    classic: {
      backLink: '← New Analytics',
    },
    comparison: {
      breadcrumb: {
        analytics: 'Advanced Analytics',
        comparison: 'Reference Comparison',
      },
      selector: {
        title: 'Reference Selection',
        description:
          'Choose a base reference and compare it with others to analyze performance metrics.',
        baseReference: {
          label: 'Base Reference',
          badge: 'Base',
          placeholder: 'Select a base reference...',
          helper: 'The base reference will serve as the comparison point for all others.',
        },
        comparisonReferences: {
          label: 'References to Compare',
          count: '{{selected}}/{{total}} selected',
          helper: 'Click on references to add or remove them from the comparison.',
        },
        summary: {
          title: '{{count}} reference(s) selected',
        },
        empty: 'No references available for comparison.',
      },
      layout: {
        pill: 'Reference comparison',
        title: 'Compare reference performance',
        subtitle:
          'Analyse key indicators to understand how each reference impacts production, quality, and operations.',
        controls: {
          primaryLabel: 'Primary reference',
          baselineBadge: 'Baseline reference',
          comparisonLabel: 'References to compare',
          comparisonCount: '{{selected}}/{{total}} selected',
          baselineIndicator: '(base)',
          selectPlaceholder: 'Select a reference...',
          multiHelper:
            'Select at least one reference to compare. The first entry acts as the baseline.',
          refresh: 'Refresh comparison',
          refreshing: 'Refreshing…',
        },
        filters: {
          title: 'Filters',
          machine: {
            label: 'Machine',
            placeholder: 'All machines',
            all: 'All machines',
          },
          lubrication: {
            label: 'Lubrication',
            placeholder: 'All lubrications',
            all: 'All lubrications',
          },
          material: {
            label: 'Material',
            placeholder: 'All materials',
            all: 'All materials',
          },
          operation: {
            label: 'Operation',
            placeholder: 'All operations',
            all: 'All operations',
          },
          clear: 'Clear',
          clearAll: 'Clear filters',
        },
        table: {
          title: 'Metrics Comparison',
          subtitle: '{{count}} reference(s) compared',
          metric: 'Metric',
          baseline: 'Baseline',
          totalDuration: 'Average Total Duration',
          totalDurationHelper: 'Average cycle duration per tool',
          numberOfParts: 'Number of Parts',
          numberOfPartsHelper: 'Total parts produced',
          volumeRemoved: 'Volume Removed',
          volumeRemovedHelper: 'Total material volume removed',
          distanceTravelled: 'Distance Travelled',
          distanceTravelledHelper: 'Total distance travelled by tool',
          malround: 'Average Malround',
          malroundHelper: 'Average circularity error',
        },
        metrics: {
          referenceTitle: 'Production — {{reference}}',
          piecesValue: '{{count}} part(s)',
          traceHelper: '{{count}} trace(s) analysed',
          averageCycle: {
            title: 'Average cycle time — {{reference}}',
            helper: 'Average machining minutes per trace.',
            value: '{{value}} min',
          },
          cycleEfficiency: {
            title: 'Cycle efficiency — {{reference}}',
            helper: 'Share of productive machining time versus total cycle duration.',
            value: '{{value}} %',
          },
          downtime: {
            title: 'Downtime rate — {{reference}}',
            helper: 'Incidents normalised per ten recent traces.',
            value: '{{value}} / 10 traces',
          },
          operatorCoverage: {
            title: 'Operator coverage — {{reference}}',
            helper: 'Dominant operator and overall operator coverage.',
            empty: 'No operator data available.',
            valueWithShare: '{{count}} operator(s). Lead: {{operator}} ({{share}}%).',
            valueWithoutShare: '{{count}} operator(s). Lead: {{operator}}.',
            valueWithoutLeader: '{{count}} operator(s) recorded.',
          },
          totalVolume: {
            title: 'Total machined volume — {{reference}}',
            helper: 'Cumulative volume across all traces.',
            value: '{{value}} cm³',
          },
          averageVolume: {
            title: 'Average volume — {{reference}}',
            helper: 'Average volume per machining cycle.',
            value: '{{value}} cm³',
          },
          totalDistance: {
            title: 'Total distance traveled — {{reference}}',
            helper: 'Cumulative distance across all traces.',
            value: '{{value}} mm',
          },
          averageDistance: {
            title: 'Average distance — {{reference}}',
            helper: 'Average distance per machining cycle.',
            value: '{{value}} mm',
          },
        },
        kpi: {
          sectionTitle: 'KPI Comparison',
          sectionTitleFallback: 'Metrics by Reference',
          labels: {
            baseline: 'Baseline',
            comparison: 'Comparison',
          },
          cycleDuration: {
            title: 'Average Cycle Duration',
            helper: 'Average time per production cycle',
          },
          cycleEfficiency: {
            title: 'Cycle Efficiency',
            helper: 'Ratio of productive machining time to total time',
          },
          downtime: {
            title: 'Downtime per 10 Traces',
            helper: 'Average incident count per 10 traces',
          },
          malround: {
            title: 'Average Malround',
            helper: 'Average roundness error',
          },
          pieces: {
            title: 'Produced Parts',
            helper: 'Total number of machined parts',
          },
        },
        chartTitles: {
          productionTrend: 'Production trend comparison',
          cycleDistribution: 'Cycle duration distribution',
          machineContribution: 'Machine contribution',
          operatorMix: 'Operator mix',
        },
        charts: {
          common: {
            minutes: '{{value}} min',
            pieces: '{{value}} part(s)',
          },
          legend: {
            showMore: 'Show {{count}} more',
            showLess: 'Show less',
            topItems: 'Top {{count}} items',
            allItems: 'All {{count}} items',
          },
          trend: {
            description: 'Track productive time and downtime for each selected reference.',
            legendProduced: 'Productive minutes — {{reference}}',
            legendDowntime: 'Downtime minutes — {{reference}}',
            empty: 'No usable trend for this selection.',
          },
          cycle: {
            description: 'Inspect cycle duration distribution to highlight differences.',
            tabLabel: 'Reference {{reference}}',
            xLabel: 'Cycle duration (min)',
            yLabel: 'Trace count',
            empty: 'No cycle duration recorded for this reference.',
            emptyAll: 'No cycle duration available for the selection.',
          },
          machine: {
            description: 'Compare machine contribution in produced parts across references.',
            legend: 'Machine — {{reference}}',
            empty: 'No machine activity available for this selection.',
            unknown: 'Machine not provided',
          },
          operator: {
            description: 'Identify the most active operators for each compared reference.',
            tabLabel: 'Reference {{reference}}',
            empty: 'No operator contribution available.',
            unknown: 'Operator not provided',
          },
        },
        trend: {
          pieces: 'Pieces',
          cycleTime: 'Cycle time',
          piecesLabel: 'Number of pieces',
          cycleTimeLabel: 'Average duration (min)',
          noData: 'No trend data available for this period.',
        },
        distribution: {
          mean: 'Mean',
          meanLabel: 'Mean {{reference}}',
          traces: 'traces',
        },
        contribution: {
          piecesValue: '{{value}} piece(s)',
          noData: 'No contribution data available for this selection.',
        },
        qualityDowntime: {
          malround: 'Malround {{reference}}',
          downtime: 'Downtime {{reference}}',
          malroundLabel: 'Average Malround',
          downtimeLabel: 'Incidents / 10 traces',
          threshold: 'Threshold',
          noValue: 'N/A',
          noData: 'No quality or downtime data available for this selection.',
        },
        summary: {
          title: 'Comparison summary',
          description:
            '{{count}} reference(s), {{totalTraces}} trace(s) and {{totalPieces}} part(s) covered by the selection.',
        },
        state: {
          loading: 'Loading comparison data…',
          empty: 'No comparison data available for this selection.',
          emptyShort: 'No metrics available for this selection.',
          noReferences: 'No active reference found yet.',
          errorTitle: 'Unable to load the comparison',
          errorDescription:
            'Fetching live data failed. Showing illustrative data until the next attempt.',
          usingSample: 'Sample data is displayed to keep visibility. Refresh to try syncing again.',
          errorDetailsTitle: 'Technical details',
          errorDetailMessage: 'Message: {{value}}',
          errorDetailCode: 'Code: {{value}}',
          errorDetailName: 'Type: {{value}}',
          errorDetailTimestamp: 'Timestamp: {{value}}',
        },
      },
      performance: {
        title: 'Reference Performance Metrics',
        description: 'Compare average metrics per tool across selected references.',
        meanDuration: {
          title: 'Mean Total Duration',
          yAxisLabel: 'Duration (min)',
          tooltip: 'Average Duration',
        },
        meanPieces: {
          title: 'Mean Piece Number',
          yAxisLabel: 'Pieces',
          tooltip: 'Average Pieces',
        },
        meanVolume: {
          title: 'Mean Volume Removed',
          yAxisLabel: 'Volume (cm³)',
          tooltip: 'Average Volume',
        },
        meanMalround: {
          title: 'Mean Malround',
          yAxisLabel: 'Malround (µm)',
          tooltip: 'Average Malround',
        },
      },
      distribution: {
        title: 'Tool Performance Distribution',
        description:
          'Analyze the distribution of tool performance metrics across selected references.',
        duration: {
          title: 'Duration Distribution',
          yAxisLabel: 'Duration',
        },
        pieces: {
          title: 'Piece Count Distribution',
          yAxisLabel: 'Pieces',
        },
        volume: {
          title: 'Volume Removed Distribution',
          yAxisLabel: 'Volume',
        },
        malround: {
          title: 'Malround Distribution',
          yAxisLabel: 'Malround (µm)',
        },
      },
    },
    charts: {
      legend: {
        showMore: 'Show {{count}} more',
        showLess: 'Show less',
        topItems: 'Top {{count}} items',
        allItems: 'All {{count}} items',
      },
      errors: {
        invalidConfig: 'Invalid chart configuration',
        noData: 'No data available',
        unknownType: 'Unknown chart type',
      },
    },
    chartError: {
      title: 'Chart rendering error',
      unexpected: 'An unexpected error occurred',
      tryAgain: 'Try again',
      stackTrace: 'Stack trace',
    },
  },
};
